import { Button, Intent, Menu, MenuItem, Popover } from "@snpjs/core";
import { Download, FileCsv, MicrosoftExcelLogo } from "@snpjs/icons";
import * as XLSX from "xlsx";
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { Table2 } from "../../components/table2/Table2";
import { ILicenseLogEntry } from "../../shared/model/ILicenseLogEntry";
import { ProtectedPage } from "../../components/pages/protectedPage/ProtectedPage";
import styles from "./AdminViewSection.module.scss";
import { canAccessAdminViewType } from "./adminViewPermissions";
import { AdminViewType } from "./types/AdminViewType";
import { Routes } from "../../routes/Routes";
import { useSession } from "../../hooks/useSession";

type ProductAttribute = { NAME?: unknown; VALUE?: unknown };

interface IProjectAnalysisProductsPageProps {
  requiredView?: AdminViewType;
}

const parseAttributes = (value: unknown): ProductAttribute[] => {
  let parsed = value;

  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
  }

  if (Array.isArray(parsed)) {
    return parsed.filter(
      (attribute): attribute is ProductAttribute =>
        attribute !== null && typeof attribute === "object"
    );
  }

  return parsed && typeof parsed === "object"
    ? Object.entries(parsed).map(([name, attributeValue]) => ({
        NAME: name,
        VALUE: attributeValue,
      }))
    : [];
};

const parseProducts = (value: string | null): ILicenseLogEntry[] => {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map((product) => {
      const normalized: ILicenseLogEntry = {};
      Object.entries(product ?? {}).forEach(([key, entryValue]) => {
        if (entryValue === null || entryValue === undefined) {
          normalized[key] = "";
        } else if (typeof entryValue === "object") {
          normalized[key] = JSON.stringify(entryValue);
        } else {
          normalized[key] = entryValue as string | number | boolean;
        }
      });
      return normalized;
    });
  } catch {
    return [];
  }
};

export const ProjectAnalysisProductsPage: React.FC<IProjectAnalysisProductsPageProps> = ({
  requiredView = AdminViewType.PROJECT_ANALYSIS,
}) => {
  const session = useSession();
  const [searchParams] = useSearchParams();
  const roles = session?.USER?.INT_AUTH_ROLE_IDS ?? [];
  const storageKey = searchParams.get("key");
  const dataParam = searchParams.get("data");
  const [selectedAttributes, setSelectedAttributes] = useState<ProductAttribute[] | null>(null);
  const products = useMemo(
    () => {
      if (storageKey) {
        return parseProducts(localStorage.getItem(storageKey));
      }

      return parseProducts(dataParam);
    },
    [dataParam, storageKey]
  );
  const [visibleCount, setVisibleCount] = useState(products.length);

  const exportData = (format: "csv" | "excel", rows: ILicenseLogEntry[] = products) => {
    if (rows.length === 0) return;

    const columns = Object.keys(rows[0] ?? {});
    const escapeCell = (value: unknown): string => {
      const text = value == null ? "" : String(value).replace(/\r?\n/g, " ");
      return `"${text.replace(/"/g, '""')}"`;
    };

    if (format === "csv") {
      const csv = [
        columns.join(","),
        ...rows.map((row) => columns.map((column) => escapeCell((row as Record<string, unknown>)[column])).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "project_products.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const workbook = XLSX.utils.book_new();
    const sheetData = rows.map((row) => {
      const values: Record<string, unknown> = {};
      columns.forEach((column) => {
        values[column] = (row as Record<string, unknown>)[column] ?? "";
      });
      return values;
    });
    const worksheet = XLSX.utils.json_to_sheet(sheetData, { header: columns });
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products");

    const workbookBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([workbookBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "project_products.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportMenu = (table?: any) => {
    const rows = table
      ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry)
      : products;

    return (
      <Menu>
        <MenuItem icon={<FileCsv />} text="CSV" onClick={() => exportData("csv", rows)} />
        <MenuItem icon={<MicrosoftExcelLogo />} text="Excel" onClick={() => exportData("excel", rows)} />
      </Menu>
    );
  };

  useEffect(() => {
    if (storageKey || dataParam) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [dataParam, storageKey]);

  if (!canAccessAdminViewType(roles, requiredView)) {
    return <Navigate to={Routes.homePage.toPath()} replace />;
  }

  return (
    <ProtectedPage
      iconName="user-circle-gear"
      className={styles.productsPageContent}
    >
      <div className={`${styles.wrapper} ${styles.productsPage}`}>
        <div className={styles.countHeader}>{visibleCount} Products</div>
        <div className={styles.productsTableContainer}>
          <Table2
            data={products}
            noResultsTitle="No products found"
            onVisibleCountChange={setVisibleCount}
            showUtilityToolbar={false}
            enableGlobalFilter={false}
            renderCell={(columnId, value) =>
              String(columnId).toUpperCase() === "ATTRIBUTES" ? (
                <Button
                  intent="primary"
                  text="Show Attributes"
                  onClick={() => setSelectedAttributes(parseAttributes(value))}
                />
              ) : undefined
            }
            leftToolbar={(table) => (
              <Popover content={exportMenu(table)} placement="bottom-start" usePortal={false}>
                <Button
                  intent={Intent.PRIMARY}
                  icon={<Download />}
                  rightIcon="caret-down"
                  text="Export"
                  fill
                  alignText="left"
                />
              </Popover>
            )}
          />
        </div>
        {selectedAttributes && (
          <div className={styles.attributesPanel}>
            <div className={styles.attributesHeader}>Attributes</div>
            <div className={styles.attributesColumnHeader}>
              <span>Name</span>
              <span>Value</span>
            </div>
            {selectedAttributes.length > 0 ? (
              <dl className={styles.attributesList}>
                {selectedAttributes.map((attribute, index) => (
                  <React.Fragment key={`${String(attribute.NAME)}-${index}`}>
                    <dt>{String(attribute.NAME ?? `Attribute ${index + 1}`)}</dt>
                    <dd>
                      {typeof attribute.VALUE === "object" && attribute.VALUE !== null
                        ? JSON.stringify(attribute.VALUE)
                        : String(attribute.VALUE ?? "")}
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            ) : (
              <div className={styles.emptyAttributes}>No attributes found.</div>
            )}
          </div>
        )}
      </div>
    </ProtectedPage>
  );
};

export default ProjectAnalysisProductsPage;